import { DefaultLayout } from '@c/layouts';
import { GetServerSideProps } from 'next';
import { getRedirectBySlug } from '@util/firestore/redirects';
import SafeImage from '@ui/SafeImage';
import React, { useState, useEffect } from 'react';
import { SPOTLIGHT_USERS, SpotlightUser } from '@util/maps/spotlightUsers';
import SpotlightCard from '@ui/SpotlightCard';

interface SpotlightPageProps {
  user: SpotlightUser;
  title?: string;
  metaTags?: Array<{
    key: string;
    property?: string;
    content?: string;
    attributes?: Record<string, string>;
  }>;
}

const getBorderClasses = (position?: string, color?: string) => {
  if (!position) return '';
  
  const positions = {
    't': 'border-t',
    'b': 'border-b',
    'l': 'border-l',
    'r': 'border-r',
    'x': 'border-x',
    'y': 'border-y',
    'all': 'border'
  };

  const colors = {
    'primary': 'border-brand-primary',
    'secondary': 'border-brand-secondary',
    'gray': 'border-brand-gray',
    'white': 'border-brand-white',
    'black': 'border-black'
  };
  
  return `${positions[position as keyof typeof positions]} ${colors[color as keyof typeof colors] || 'border-brand-primary'}`;
};

const SpotlightPage = ({ user }: SpotlightPageProps) => {
  const VisitCards = () => (
    <div className="mt-12 mb-[6rem] flex justify-center gap-12 sm:gap-12 gap-4">
      {/* YouTube Card */}
      {user.youtube_url && (
        <SpotlightCard
          url={user.youtube_url}
          profileImage={user.profile_image}
          name={user.name}
          logoSrc="/images/spotlight/icons/youtube-logo.png"
          logoAlt="YouTube"
          logoWidth={80}
          customTitle={`${user.name.toUpperCase()}'S`}
        />
      )}
      
      {/* External Site Card */}
      {user.external_site_url && (
        <SpotlightCard
          url={user.external_site_url}
          profileImage={user.profile_image}
          name={user.name}
          logoSrc="/images/spotlight/external-site.png"
          logoAlt="External Site"
          logoWidth={60}
          customTitle={user.external_site_name}
        />
      )}

      {/* Embeded Modal */}
      {user.embed_modal && (
        <SpotlightCard
          profileImage={user.profile_image}
          name={user.name}
          logoSrc="/images/spotlight/external-site.png"
          logoAlt="External Site"
          logoWidth={60}
          customTitle={user.embed_modal.heading}
          embed={{
            closeIconColor: user.embed_modal.closeIconColor,
            bgImage: user.embed_modal.bgImage,
            html: user.embed_modal.html,
            includeHeading: user.embed_modal.includeHeader
          }}
        />
      )}
      
      {/* GearShop Card */}
      <SpotlightCard
        url={user.store_url}
        profileImage={user.profile_image}
        name={user.name}
        logoSrc="/images/spotlight/icons/gearshop-logo.png"
        logoAlt="GearShop"
        logoWidth={120}
      />
    </div>
  );

  const Carousel = ({ items }: { items: Array<{ mainImage: string; thumbnailImage: string; url: string; alt: string; }> }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentIndex((current) => (current + 1) % items.length);
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(interval);
    }, [items.length]);

    return (
      <div className="relative w-full">
        {/* Main Images */}
        <div className="relative w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] overflow-hidden rounded-[1rem] mx-auto">
          {items.map((item, index) => (
            <a 
              key={index}
              href={item.url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`absolute inset-0 transition-opacity duration-500 ${
                index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <SafeImage
                src={item.mainImage}
                alt={item.alt}
                fill
                className="object-cover"
                priority
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-black/60"></div>
              {/* Product Info */}
              <div className="absolute bottom-8 left-8">
                <p className="text-[1.4rem] font-medium uppercase tracking-wider text-brand-white/80">
                  Featured Gear
                </p>
                <p className="text-[1.8rem] font-bold text-brand-white">
                  {item.alt}
                </p>
              </div>
            </a>
          ))}
        </div>

        {/* Thumbnails */}
        <div className="mt-4 flex w-full justify-center gap-4 overflow-x-auto pb-2">
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`relative flex-shrink-0 transition-all ${
                currentIndex === index 
                  ? 'opacity-100' 
                  : 'opacity-40 hover:opacity-100'
              }`}
            >
              <div className="h-24 w-24 overflow-hidden rounded-[1rem]">
                <SafeImage
                  src={item.thumbnailImage}
                  alt={`${item.alt} thumbnail`}
                  width={96}
                  height={96}
                  className="h-full w-full object-cover"
                />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <div className="text-[2rem] text-brand-gray">User not found</div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center">
      {/* Hero Banner */}
      <div className="relative h-[48rem] w-full overflow-hidden">
        <div className="absolute inset-0 bg-black/30 z-10"></div>
        <SafeImage
          src={user.banner_image || user.store_photo || 'https://gearfocus.com/gear_pile.png'}
          alt={`${user.name}&apos;s workspace`}
          className="h-full w-full object-cover grayscale contrast-125"
          fill
          priority
        />
        <div className="absolute left-0 top-0 mx-auto max-w-[1000px] p-12 text-brand-white z-20">
          <p className="mb-4 text-[1.4rem] font-medium uppercase tracking-wider">Creator Spotlight</p>
          <h1 className="text-[3.6rem] font-bold leading-tight">{user.name}</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto w-full px-4 py-20 max-w-[1200px]">
        <div className="mb-16">
          <h2 className="text-[3.75rem] font-bold text-center leading-none mb-2">
            GearFocus Creator Spotlight: {user.name}
          </h2>
          <p className="mb-[5rem] text-[3.25rem] font-medium text-center italic leading-none">
            { user.subtitle || 'No subtitle provided yet.' }
          </p>

          {/* Bio Section */}
          <div className="text-[1.8rem] leading-relaxed">
            {user.bio || 'No bio provided yet.'}
          </div>
        </div>

        {/* Story Sections */}
        {user.story_sections?.map((section, index) => (
          <div 
            key={index} 
            className={`mb-12 ${section.border ? getBorderClasses(section.borderPosition, section.borderColor) : ''} ${section.className || ''}`}
            style={section.style || {}}
          >
            {section.title && (
              <h3 className="mb-4 text-[2.4rem] font-bold text-brand-secondary">{section.title}</h3>
            )}
            
            {/* Regular content if no special layout */}
            {!section.layout && !section.carousel && !section.isImage && !section.isYouTube && !section.isBannerQuote && (
              <>
                <p className="text-[1.8rem] leading-relaxed" style={section.paragraphStyle || {}}>
                  {section.content}
                </p>
                {section.list && (
                  <ul className={`mt-8 space-y-4 text-[1.8rem] leading-relaxed ${
                    section.listStyle === 'number' ? 'list-decimal' :
                    section.listStyle === 'bullet' ? 'list-disc' :
                    'list-none'
                  } ml-8`}>
                    {section.list.map((item, i) => (
                      <li 
                        key={i} 
                        className="pl-2"
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ))}
                  </ul>
                )}
              </>
            )}

            {/* Image-only section */}
            {section.isImage && (
              <div className={`flex justify-center ${section.link ? 'cursor-pointer' : ''}`} onClick={() => {
                if (section.link) {
                  window.open(section.link, '_blank');
                }
              }}>
                <div className="relative" style={{
                  width: section.imageWidth ? `${section.imageWidth}px` : 'auto',
                  height: section.imageHeight ? `${section.imageHeight}px` : 'auto'
                }}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={section.content}
                    alt={section.imageAlt || ''}
                    width={section.imageWidth}
                    height={section.imageHeight}
                    className="rounded-xl"
                  />
                </div>
              </div>
            )}

            {/* YouTube section */}
            {section.isYouTube && (
              <div className="w-full">
                <div className={`relative w-full ${
                  section.aspectRatio === '4:3' ? 'aspect-[4/3]' :
                  section.aspectRatio === '1:1' ? 'aspect-square' :
                  'aspect-video'
                }`}>
                  <iframe
                    src={section.content}
                    title="YouTube video"
                    className="absolute inset-0 w-full h-full rounded-[1rem]"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Carousel */}
            {section.carousel && (
              <div className="my-16">
                <Carousel items={section.carousel} />
              </div>
            )}

            {/* Multi-column layouts */}
            {section.layout && section.columns && (
              <div className={`my-16 grid grid-cols-1 gap-12 ${
                section.layout === 'two-columns' ? 'md:grid-cols-2' : 
                section.layout === 'three-columns' ? 'md:grid-cols-3' : ''
              }`}>
                {section.columns.map((column, colIndex) => (
                  <div 
                    key={colIndex} 
                    className={`${column.quote ? 'relative' : ''} flex justify-center ${column.className || ''}`}
                    style={column.style || {}}
                  >
                    {column.isYouTube ? (
                      <div className="w-full">
                        <div className={`relative w-full ${
                          column.aspectRatio === '4:3' ? 'aspect-[4/3]' :
                          column.aspectRatio === '1:1' ? 'aspect-square' :
                          'aspect-video'
                        }`}>
                          <iframe
                            src={column.content}
                            title="YouTube video"
                            className="absolute inset-0 w-full h-full rounded-[1rem]"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    ) : column.isImage ? (
                      <div className="relative" style={{
                        width: column.imageWidth ? `${column.imageWidth}px` : 'auto',
                        height: column.imageHeight ? `${column.imageHeight}px` : 'auto'
                      }}>
                        <SafeImage
                          src={column.content}
                          alt={column.imageAlt || ''}
                          width={column.imageWidth}
                          height={column.imageHeight}
                          className="rounded-xl"
                        />
                      </div>
                    ) : (
                      <>
                        {column.quote && (
                          <div className="absolute -left-4 top-0 text-[8rem] text-brand-primary/80">
                            &ldquo;
                          </div>
                        )}
                        <div className={`${column.border ? getBorderClasses(column.borderPosition, column.borderColor) : ''} ${
                          column.borderPosition ? 'pt-8' : ''
                        }`}>
                          <div className={`text-[1.8rem] leading-relaxed ${
                            column.quote 
                              ? 'text-[2rem] font-medium italic leading-relaxed text-brand-secondary'
                              : ''
                          }`} style={column.paragraphStyle || {}}>
                            <div dangerouslySetInnerHTML={{ __html: column.content }} />
                            {column.quote && (
                              <footer className="mt-4 text-[1.6rem] font-semibold text-brand-secondary/80">
                                &ndash; {user.name}
                              </footer>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Visit Cards */}
            {section.renderVisitCards && <VisitCards />}

            {/* Banner Quote */}
            {section.isBannerQuote && (
              <div className="my-24 flex flex-col items-center justify-center gap-8">
                <div className="h-[1px] w-[35%] bg-black"></div>
                <div className="text-[1.8rem] font-medium text-center max-w-[800px]">
                  <p className="px-[2rem]">{section.content}</p>
                </div>
                <div className="h-[1px] w-[35%] bg-black"></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

SpotlightPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getServerSideProps: GetServerSideProps<SpotlightPageProps> = async (
  context
) => {
  try {
    if (!context.params?.spotlight) {
      return { notFound: true };
    }

    const { spotlight } = context.params;
    const user = SPOTLIGHT_USERS[spotlight.toString()];

    if (!user) {
      const { results: redirects } = await getRedirectBySlug({ slug: `/s/${spotlight}` });

      if (redirects) {
        return {
          redirect: {
            destination: redirects.destination,
            permanent: redirects.permanent
          }
        }
      } else {
        return {
          redirect: {
            destination: '/products',
            permanent: false
          }
        }
      }
    }

    return {
      props: {
        user,
        title: `${user.name} | Community Spotlight | GearFocus`,
        metaTags: [
          {
            key: 'og:title',
            property: 'og:title',
            content: `${user.name} | Community Spotlight | GearFocus`,
          },
          {
            key: 'og:description',
            property: 'og:description',
            content: user.bio || "Featured member of the GearFocus community.",
          },
          {
            key: 'og:image',
            property: 'og:image',
            content: user.store_photo ?? 'https://gearfocus.com/gear_pile.png',
          },
          {
            key: 'twitter:image',
            attributes: { name: 'twitter:image' },
            content: user.store_photo ?? 'https://gearfocus.com/gear_pile.png',
          },
          {
            key: 'twitter:title',
            attributes: { name: 'twitter:title' },
            content: `${user.name} | Community Spotlight | GearFocus`,
          },
        ],
      },
    };
  } catch (error) {
    console.error('Error fetching spotlight user:', error);
    return {
      redirect: {
        destination: '/products',
        permanent: false
      }
    };
  }
};

export default SpotlightPage;
